import { z } from "zod";
const MODEL_PLACEHOLDERS = new Set([
    "openrouter/video-gen-default",
    "openrouter/default",
    "flowgen/default",
    "default",
    "",
]);
const modelSchema = z
    .union([z.literal(""), z.string().min(1).max(200)])
    .refine((v) => MODEL_PLACEHOLDERS.has(v) || /^[a-z0-9._-]+\/[a-z0-9._-]+$/i.test(v), { message: "Model must look like \"provider/model-name\" (e.g. google/veo-3.1-lite) or be left blank to use the default" });
export function normaliseModel(input) {
    if (input == null)
        return null;
    const v = input.trim();
    if (MODEL_PLACEHOLDERS.has(v.toLowerCase()))
        return null;
    return v;
}
export const sceneParametersSchema = z.object({
    aspectRatio: z.enum(["16:9", "9:16", "1:1", "4:3"]).optional(),
    style: z.string().max(200).optional(),
    camera: z.string().max(200).optional(),
    fps: z.number().int().min(8).max(60).optional(),
    motionIntensity: z.enum(["low", "medium", "high"]).optional(),
    negativePrompt: z.string().max(1000).optional(),
    seed: z.number().int().optional(),
    generateAudio: z.boolean().optional(),
});
/** Local uploads are `/media/...`; remote refs are http(s) URLs. */
const referenceImageUrlSchema = z
    .string()
    .min(1)
    .max(2000)
    .refine((v) => v.startsWith("/media/") || /^https?:\/\//i.test(v), { message: "Must be a /media/... path or http(s) URL" });
export const characterSchema = z.object({
    name: z.string().min(1).max(80),
    description: z.string().max(2000).default(""),
    tags: z.array(z.string().min(1).max(40)).max(20).default([]),
    referenceImageUrls: z.array(referenceImageUrlSchema).max(8).default([]),
});
export const characterUpdateSchema = characterSchema.partial();
export const settingSchema = z.object({
    name: z.string().min(1).max(80),
    description: z.string().max(2000).default(""),
    tags: z.array(z.string().min(1).max(40)).max(20).default([]),
    referenceImageUrls: z.array(referenceImageUrlSchema).max(8).default([]),
});
export const settingUpdateSchema = settingSchema.partial();
export const projectDefaultsSchema = z.object({
    aspectRatio: z.enum(["16:9", "9:16", "1:1", "4:3"]).optional(),
    style: z.string().max(200).optional(),
    camera: z.string().max(200).optional(),
    motionIntensity: z.enum(["low", "medium", "high"]).optional(),
    styleBibleUrl: referenceImageUrlSchema.optional(),
});
export const sceneKeyframeSchema = z.object({
    id: z.string().min(1).max(80),
    url: referenceImageUrlSchema,
    useAsReference: z.boolean().default(false),
    role: z.enum(["none", "first", "middle", "last"]).default("none"),
});
export const sceneSchema = z.object({
    title: z.string().min(1).max(140),
    settingId: z.string().nullable().optional(),
    characterIds: z.array(z.string()).max(12).default([]),
    storyboard: z.string().min(1).max(4000),
    durationSeconds: z.number().min(1).max(60).default(6),
    model: modelSchema.optional(),
    parameters: sceneParametersSchema.default({}),
    continuesFromSceneId: z.string().nullable().optional(),
    continueEnabled: z.boolean().optional(),
    keyframes: z.array(sceneKeyframeSchema).max(16).default([]),
});
export const sceneUpdateSchema = sceneSchema.partial();
export const sceneGenerationRequestSchema = z.object({
    sceneId: z.string(),
});
export const editSequenceSchema = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    orderedVideoIds: z.array(z.string()).default([]),
});
export const editSequenceUpdateSchema = editSequenceSchema.partial();
export const projectSchema = z.object({
    name: z.string().min(1).max(120),
    defaults: projectDefaultsSchema.optional(),
});
export const projectUpdateSchema = projectSchema.partial();
export const beatKindSchema = z.enum([
    "establish",
    "introduce",
    "conversation",
    "action",
    "reaction",
    "dead_air",
    "button",
]);
export const beatSlotsSchema = z
    .object({
    setting: z.string().max(80).nullable().optional(),
    mood: z.string().max(200).optional(),
    detail: z.string().max(800).optional(),
    trope: z.string().max(40).optional(),
    a: z.string().max(80).optional(),
    b: z.string().max(80).optional(),
    a_line: z.string().max(400).optional(),
    b_line: z.string().max(400).optional(),
    speaker: z.string().max(80).optional(),
    listener: z.string().max(80).optional(),
    move: z.string().max(40).optional(),
    line: z.string().max(500).optional(),
    after: z.enum(["none", "reaction", "dead_air"]).or(z.string().max(20)).optional(),
    who: z.string().max(80).optional(),
    to: z.string().max(200).optional(),
    flavor: z.string().max(200).optional(),
    silent: z.boolean().optional(),
    doing: z.string().max(300).optional(),
    image: z.string().max(500).optional(),
    sting: z.string().max(300).optional(),
})
    .passthrough();
export const storyBeatSchema = z.object({
    id: z.string().min(1).max(80).optional(),
    title: z.string().min(1).max(140),
    summary: z.string().min(1).max(2000),
    durationSeconds: z.number().min(1).max(60).default(6),
    suggestedCharacterNames: z.array(z.string().min(1).max(80)).max(12).default([]),
    suggestedSettingName: z.string().max(80).nullable().optional(),
    sceneId: z.string().nullable().optional(),
    sceneIds: z.array(z.string()).max(8).optional(),
    kind: beatKindSchema.nullable().optional(),
    slots: beatSlotsSchema.nullable().optional(),
});
export const storyOutlineSchema = z.object({
    title: z.string().min(1).max(200),
    logline: z.string().max(1000).default(""),
    tone: z.string().max(400).default(""),
    targetDurationSeconds: z.number().min(1).max(600).default(60),
    beats: z.array(storyBeatSchema).min(1).max(24),
});
export const storyOutlineUpdateSchema = storyOutlineSchema.partial();
export const agentRunCreateSchema = z.object({
    pitch: z.string().min(1).max(12000),
    pauseBeforeVideo: z.boolean().optional().default(true),
    projectName: z.string().min(1).max(120).optional(),
});
export const agentApproveSchema = z.object({
    note: z.string().max(1000).optional(),
});
export const generateTurnaroundSchema = z.object({
    subjectDescription: z.string().min(1).max(2000),
    model: z.string().max(200).optional(),
    /** Character sheet vs place establishing stills. */
    kind: z.enum(["character", "place"]).optional().default("character"),
    /** When set, append produced URLs onto this character/setting. */
    attachTo: z
        .object({
        type: z.enum(["character", "setting"]),
        id: z.string().min(1),
    })
        .optional(),
});
export const generateKeyframeSchema = z.object({
    prompt: z.string().max(2000).optional(),
    angleId: z.string().max(80).optional(),
    cameraInstruction: z.string().max(1000).optional(),
    role: z.enum(["none", "first", "middle", "last"]).optional().default("none"),
    useAsReference: z.boolean().optional().default(true),
    model: z.string().max(200).optional(),
    sourceKeyframeId: z.string().optional(),
});
export const authCredentialsSchema = z.object({
    email: z.string().email().max(200),
    password: z.string().min(8).max(200),
});
//# sourceMappingURL=schemas.js.map